import React, { useRef, useState } from 'react';
import {
  Box, Button, Checkbox, CircularProgress, FormControl, FormControlLabel, FormLabel,
  Grid, IconButton, Paper, Radio, RadioGroup, TextField, Typography,
} from '@mui/material';
import {
  AddAPhoto as AddPhotoIcon, Close as CloseIcon, ZoomIn as ZoomInIcon,
} from '@mui/icons-material';
import type { PhysioPostureAssessment, PosturalPhotoViewKey } from '../../types/physiotherapyAssessment';
import {
  POSTURAL_ANTERIOR_LOWER_BODY,
  POSTURAL_ASYMMETRY_OPTIONS,
  POSTURAL_LATERAL_FINDINGS,
  POSTURAL_PHOTO_VIEWS,
  POSTURAL_POSTERIOR_FINDINGS,
} from '../../constants/posturalAssessmentSchema';
import { uploadPosturalAssessmentPhoto } from '../../services/posturalAssessmentPhotoService';
import { useClinicPageBreakpoints } from '../../hooks/useClinicPageBreakpoints';
import { PATIENT_PANEL_THEME } from '../patientPanelTheme';

const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' },
};

const photoSlotSx = {
  border: '2px dashed #cbd5e1',
  borderRadius: 2,
  bgcolor: '#f8fafc',
  minHeight: 200,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  position: 'relative',
};

function FindingCheckboxes({
  title,
  options,
  selected,
  onChange,
}: {
  title: string;
  options: ReadonlyArray<{ key: string; label: string }>;
  selected: string[];
  onChange: (keys: string[]) => void;
}) {
  const toggle = (key: string) => {
    onChange(selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]);
  };
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', display: 'block', mb: 0.75 }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
        {options.map((opt) => (
          <FormControlLabel
            key={opt.key}
            control={<Checkbox size="small" checked={selected.includes(opt.key)} onChange={() => toggle(opt.key)} />}
            label={<Typography variant="caption">{opt.label}</Typography>}
          />
        ))}
      </Box>
    </Box>
  );
}

function PhotoSlot({
  label,
  photo,
  uploading,
  onPick,
  onRemove,
  onPreview,
  readOnly = false,
}: {
  label: string;
  photo: PhysioPostureAssessment['photos'][PosturalPhotoViewKey];
  uploading: boolean;
  onPick: (file: File) => void;
  onRemove: () => void;
  onPreview: () => void;
  readOnly?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 700, color: PATIENT_PANEL_THEME.sectionTitle, mb: 0.75, display: 'block' }}>
        {label}
      </Typography>
      <Paper elevation={0} sx={photoSlotSx}>
        {uploading ? (
          <CircularProgress size={28} />
        ) : photo?.file_url ? (
          <>
            <Box
              component="img"
              src={photo.file_url}
              alt={label}
              sx={{ width: '100%', maxHeight: 220, objectFit: 'contain', cursor: 'pointer' }}
              onClick={onPreview}
            />
            <Box sx={{ display: 'flex', gap: 0.5, p: 1 }}>
              <Button size="small" startIcon={<ZoomInIcon />} onClick={onPreview} sx={{ textTransform: 'none' }}>
                Ampliar
              </Button>
              {!readOnly && (
                <>
                  <Button size="small" onClick={() => inputRef.current?.click()} sx={{ textTransform: 'none' }}>
                    Substituir
                  </Button>
                  <IconButton size="small" onClick={onRemove} aria-label="Remover foto">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Box>
          </>
        ) : readOnly ? (
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>Sem foto</Typography>
        ) : (
          <Button
            variant="outlined"
            startIcon={<AddPhotoIcon />}
            onClick={() => inputRef.current?.click()}
            sx={{ textTransform: 'none', borderColor: '#94a3b8', color: '#475569' }}
          >
            Anexar foto
          </Button>
        )}
        {!readOnly && (
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onPick(file);
              e.target.value = '';
            }}
          />
        )}
      </Paper>
    </Box>
  );
}

export interface PosturalAssessmentSectionProps {
  value: PhysioPostureAssessment;
  onChange: (next: PhysioPostureAssessment) => void;
  patientId?: string;
  readOnly?: boolean;
}

const PosturalAssessmentSection: React.FC<PosturalAssessmentSectionProps> = ({
  value,
  onChange,
  patientId,
  readOnly = false,
}) => {
  const { isMobile } = useClinicPageBreakpoints();
  const [uploadingView, setUploadingView] = useState<PosturalPhotoViewKey | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const setPosture = (patch: Partial<PhysioPostureAssessment>) => onChange({ ...value, ...patch });

  const setPhoto = (view: PosturalPhotoViewKey, photo: PhysioPostureAssessment['photos'][PosturalPhotoViewKey]) => {
    onChange({ ...value, photos: { ...value.photos, [view]: photo } });
  };

  const handleUpload = async (view: PosturalPhotoViewKey, file: File) => {
    if (!patientId) {
      setUploadError('Paciente não identificado para anexar fotos.');
      return;
    }
    setUploadError(null);
    setUploadingView(view);
    try {
      const photo = await uploadPosturalAssessmentPhoto(patientId, file, view);
      setPhoto(view, photo);
    } catch (e: any) {
      setUploadError(e?.response?.data?.error || e?.message || 'Erro ao enviar foto');
    } finally {
      setUploadingView(null);
    }
  };

  const readOnlySx = readOnly ? {
    '& .MuiCheckbox-root, & .MuiRadio-root': { pointerEvents: 'none' },
    '& .MuiInputBase-input, & .MuiInputBase-inputMultiline': { pointerEvents: 'none' },
  } : undefined;

  return (
    <Box sx={readOnlySx}>
      {uploadError && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
          {uploadError}
        </Typography>
      )}

      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f766e', mb: 1.5 }}>
        Visão lateral
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {(['lateral_right', 'lateral_left'] as PosturalPhotoViewKey[]).map((view) => {
          const meta = POSTURAL_PHOTO_VIEWS.find((v) => v.key === view)!;
          return (
            <Grid item xs={12} md={6} key={view}>
              <PhotoSlot
                label={meta.label}
                photo={value.photos[view]}
                uploading={uploadingView === view}
                onPick={(file) => handleUpload(view, file)}
                onRemove={() => setPhoto(view, null)}
                onPreview={() => value.photos[view]?.file_url && setPreviewUrl(value.photos[view]!.file_url)}
                readOnly={readOnly}
              />
            </Grid>
          );
        })}
      </Grid>
      <FindingCheckboxes
        title="Achados — visão lateral"
        options={POSTURAL_LATERAL_FINDINGS}
        selected={value.lateral_findings}
        onChange={(keys) => setPosture({ lateral_findings: keys })}
      />

      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f766e', mb: 1.5, mt: 1 }}>
        Visão posterior
      </Typography>
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid item xs={12} md={5}>
          <PhotoSlot
            label="Visão posterior"
            photo={value.photos.posterior}
            uploading={uploadingView === 'posterior'}
            onPick={(file) => handleUpload('posterior', file)}
            onRemove={() => setPhoto('posterior', null)}
            onPreview={() => value.photos.posterior?.file_url && setPreviewUrl(value.photos.posterior!.file_url)}
            readOnly={readOnly}
          />
        </Grid>
        <Grid item xs={12} md={7}>
          <FindingCheckboxes
            title="Achados — visão posterior"
            options={POSTURAL_POSTERIOR_FINDINGS}
            selected={value.posterior_findings}
            onChange={(keys) => setPosture({ posterior_findings: keys })}
          />
        </Grid>
      </Grid>

      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f766e', mb: 1.5, mt: 1 }}>
        Visão anterior
      </Typography>
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid item xs={12} md={5}>
          <PhotoSlot
            label="Visão anterior"
            photo={value.photos.anterior}
            uploading={uploadingView === 'anterior'}
            onPick={(file) => handleUpload('anterior', file)}
            onRemove={() => setPhoto('anterior', null)}
            onPreview={() => value.photos.anterior?.file_url && setPreviewUrl(value.photos.anterior!.file_url)}
            readOnly={readOnly}
          />
        </Grid>
        <Grid item xs={12} md={7}>
          <FindingCheckboxes
            title="Joelhos e pés"
            options={POSTURAL_ANTERIOR_LOWER_BODY}
            selected={value.anterior_lower_body}
            onChange={(keys) => setPosture({ anterior_lower_body: keys })}
          />
          <FormControl sx={{ mb: 1.5 }}>
            <FormLabel sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Assimetria de quadril</FormLabel>
            <RadioGroup
              row={!isMobile}
              value={value.hip_asymmetry}
              onChange={(e) => setPosture({ hip_asymmetry: e.target.value as PhysioPostureAssessment['hip_asymmetry'] })}
            >
              {POSTURAL_ASYMMETRY_OPTIONS.map((opt) => (
                <FormControlLabel key={opt.value} value={opt.value} control={<Radio size="small" />} label={opt.label} />
              ))}
            </RadioGroup>
          </FormControl>
          <FormControl>
            <FormLabel sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Ombros assimétricos</FormLabel>
            <RadioGroup
              row={!isMobile}
              value={value.shoulder_asymmetry}
              onChange={(e) => setPosture({ shoulder_asymmetry: e.target.value as PhysioPostureAssessment['shoulder_asymmetry'] })}
            >
              {POSTURAL_ASYMMETRY_OPTIONS.map((opt) => (
                <FormControlLabel key={`sh-${opt.value}`} value={opt.value} control={<Radio size="small" />} label={opt.label} />
              ))}
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>

      <TextField
        fullWidth
        multiline
        minRows={3}
        size="small"
        label="Observações acerca da avaliação postural"
        value={value.observations}
        onChange={(e) => setPosture({ observations: e.target.value })}
        sx={{ mt: 1, ...fieldSx }}
      />

      {previewUrl && (
        <Box
          onClick={() => setPreviewUrl(null)}
          sx={{
            position: 'fixed', inset: 0, bgcolor: 'rgba(15,23,42,0.85)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, cursor: 'zoom-out',
          }}
        >
          <Box component="img" src={previewUrl} alt="Pré-visualização" sx={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 2 }} />
        </Box>
      )}
    </Box>
  );
};

export default PosturalAssessmentSection;
